import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { useAuthContext } from "@/contexts/AuthContext";
import { PUBLIC_HOLIDAY_TYPE, type PublicHoliday } from "@/types/publicHoliday";

// Country used to list holidays. Public holidays are tenant-isolated server-side;
// the country list endpoint returns every holiday type for the org, so the hook
// filters down to PUBLIC_HOLIDAY client-side (SCHOOL_CLOSURE / DAYCARE_CLOSURE
// are managed on other surfaces).
const HOLIDAY_COUNTRY = "CH";

// Keys are scoped by orgId so a user with access to multiple orgs (Clerk
// multi-org session) never reads another org's cached holidays. The list
// endpoint is tenant-isolated server-side; the orgId segment keeps the client
// cache isolated too. The broad `all` prefix still invalidates every org's
// entries.
export const publicHolidayKeys = {
  all: ["public-holidays"] as const,
  lists: (orgId: string) => [...publicHolidayKeys.all, "list", orgId] as const,
  list: (orgId: string) => [...publicHolidayKeys.lists(orgId)] as const,
  details: (orgId: string) => [...publicHolidayKeys.all, "detail", orgId] as const,
  detail: (orgId: string, id: string) =>
    [...publicHolidayKeys.details(orgId), id] as const,
};

// usePublicHolidays fetches every active PUBLIC_HOLIDAY entry for the org. The
// country list endpoint is unpaginated (the dataset is small reference data), so
// the page filters client-side via BaseEditableTable's search box.
export function usePublicHolidays() {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();
  return useQuery({
    queryKey: publicHolidayKeys.list(orgId ?? ""),
    queryFn: async (): Promise<PublicHoliday[]> => {
      const { HolidaysService } = await import("@/api");
      const response = await HolidaysService.getApiV1HolidaysCountry(HOLIDAY_COUNTRY);
      const holidays = response.holidays ?? [];
      return holidays.filter((h) => h.holidayType === PUBLIC_HOLIDAY_TYPE);
    },
    enabled: isSignedIn && isAuthReady && !!orgId,
    staleTime: 5 * 60 * 1000,
  });
}

export const closureDateKeys = {
  all: ["holiday-closures"] as const,
  list: (orgId: string) => [...closureDateKeys.all, "list", orgId] as const,
};

// useClosureDates returns every date the daycare is shut — PUBLIC_HOLIDAY *and*
// SCHOOL_CLOSURE / DAYCARE_CLOSURE. `usePublicHolidays` deliberately filters the last two
// out because the holidays admin page only manages public holidays, but a scheduling
// surface that generates attendance days must skip all of them: generating a settling-in
// day on a day the daycare is closed produces a day nobody can attend.
export function useClosureDates() {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();
  return useQuery({
    queryKey: closureDateKeys.list(orgId ?? ""),
    queryFn: async (): Promise<PublicHoliday[]> => {
      const { HolidaysService } = await import("@/api");
      const response = await HolidaysService.getApiV1HolidaysCountry(HOLIDAY_COUNTRY);
      return response.holidays ?? [];
    },
    enabled: isSignedIn && isAuthReady && !!orgId,
    staleTime: 5 * 60 * 1000,
  });
}
